import { uid } from '../../../../lib/uid';
import type { CompetencyLevel, PersonnelCatalogEntry } from '../personnelTypes';
import { SKILL_PROFICIENCY_LEVELS } from '../personnelTypes';

/*
  Plausible skills, which is the right call here for the reason `sampleTraining`'s course titles
  and `sampleEducation`'s school names are: a skill never leaves `rsys_` and never lands in the
  identity store, so an obviously synthetic one would make the generated profile worse at the one
  job it has. The rule is about where a value LANDS, not which generator it came from —
  `samplePersonnel` and `sampleFamilyMember` write into `rsys_pii` and are the two that must not.
*/
const SKILLS = [
  'Public Speaking',
  'Photography',
  'Programming',
  'Cooking',
  'Basketball',
  'Technical Writing',
  'Graphic Design',
  'Choral Singing',
];

export interface SampleSpecialSkill {
  name: string;

  /** Null only where the catalog is empty — there is no id to invent. */
  categoryId: string | null;

  proficiencyLevel: CompetencyLevel;
  yearsOfExperience: string;
  remarks: string;
}

/**
 * Test data for the Special skills/Hobbies form's Autogenerate.
 *
 * **The category is a REAL row the modal already loaded**, which is what the foreign-key rule
 * asks for: an id this generator invented would fail the server's "it must be a live row" check,
 * and that failure reads as a bug in the form rather than in the generator. An empty catalog
 * yields null and the caller leaves the picker alone.
 *
 * **The proficiency is drawn from `SKILL_PROFICIENCY_LEVELS`, so `0` can never come out of it.**
 * Not rated is refused server-side, and a generator that produced one would fill a form the
 * reader then has to correct before it saves — the one thing Autogenerate exists to prevent.
 *
 * **The name is suffixed.** Autogenerate is most useful clicked repeatedly, and one live skill
 * per employee is a unique index — without the suffix the second click would produce a row the
 * server rejects as a duplicate.
 *
 * **It fills the optional fields too.** The floor is that the primary action enables; the point
 * is that the screens behind the form get exercised, and a record carrying only its required
 * fields produces a panel of em dashes that exercises nothing.
 */
export function sampleSpecialSkill(
  categories: readonly PersonnelCatalogEntry[],
): SampleSpecialSkill {
  const tag = uid().slice(0, 6).toUpperCase();
  const skill = SKILLS[Math.floor(Math.random() * SKILLS.length)];

  const category =
    categories.length > 0 ? categories[Math.floor(Math.random() * categories.length)] : null;

  const proficiency =
    SKILL_PROFICIENCY_LEVELS[Math.floor(Math.random() * SKILL_PROFICIENCY_LEVELS.length)];

  return {
    name: `${skill} ${tag}`,
    categoryId: category?.id ?? null,
    proficiencyLevel: proficiency,

    /*
      Never zero, and never blank. Zero is a meaningful answer on this field — somebody who took
      it up this year — so generating one would put a real claim on test data; blank is the other
      meaningful answer and would leave the column unexercised.
    */
    yearsOfExperience: String(1 + Math.floor(Math.random() * 15)),
    remarks: `Practised outside work. Generated sample ${tag}.`,
  };
}
